import React, { useCallback, useEffect, useState } from 'react';
import { EyeIcon, PencilSquareIcon, PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PushPanelShell from './PushPanelShell';
import PushNotificationDevicePreview from './PushNotificationDevicePreview';
import type { PushNotificationTemplate } from '../../../models/MasterAdmin/pushNotificationHub';
import { PUSH_HUB_BASE_PATH } from '../../../models/MasterAdmin/pushNotificationHub';
import { deletePushTemplate, fetchPushTemplates } from '../../../services/MasterAdmin/pushNotificationHubService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';

const PushTemplatesPanel: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<PushNotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState<PushNotificationTemplate | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchPushTemplates();
      setItems(res.data.items || []);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to load templates'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = items.filter((row) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return `${row.name} ${row.title} ${row.message}`.toLowerCase().includes(q);
  });

  const onDelete = async (row: PushNotificationTemplate) => {
    if (!window.confirm(`Delete template "${row.name}"? Campaigns keep their copied content.`)) return;
    try {
      await deletePushTemplate(row.id);
      toast.success('Template removed.');
      await load();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Delete failed'));
    }
  };

  return (
    <PushPanelShell
      title="Templates"
      description="Create notification content here (title, message, images, default tap destination). Campaigns only select a template."
      actions={
        <button
          type="button"
          onClick={() => navigate(`${PUSH_HUB_BASE_PATH}/templates/new`)}
          className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4" />
          New template
        </button>
      }
    >
      <div className="mb-4">
        <input
          className="w-full max-w-md rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          placeholder="Search templates…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Template</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Opens</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">
                  No templates yet. Create one before starting a campaign.
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr key={row.id} className="align-top">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 dark:text-white">{row.name}</p>
                    <p className="mt-0.5 text-sm text-gray-700 dark:text-gray-200">{row.title}</p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">{row.message}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">{row.notificationType}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600 dark:text-gray-300">
                    {row.intentType}
                    {row.intentValue ? ` · ${row.intentValue}` : ''}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        row.isActive
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                      }`}
                    >
                      {row.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setViewing(row)}
                      className="inline-flex rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="View preview"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`${PUSH_HUB_BASE_PATH}/templates/${row.id}/edit`)}
                      className="inline-flex rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                      title="Edit"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => void onDelete(row)}
                      className="inline-flex rounded-lg p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {viewing ? (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
          <button type="button" className="absolute inset-0 bg-black/50" aria-label="Close" onClick={() => setViewing(null)} />
          <div className="relative z-10 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-gray-200 bg-white p-5 shadow-xl dark:border-gray-700 dark:bg-gray-900">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{viewing.name}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {viewing.notificationType} · {viewing.intentType}
                </p>
              </div>
              <button type="button" onClick={() => setViewing(null)} className="rounded-lg p-1 text-gray-500 hover:bg-gray-100">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-xl bg-gray-50 p-3 text-sm dark:bg-gray-800/60">
                <p className="font-semibold text-gray-900 dark:text-white">{viewing.title}</p>
                <p className="mt-2 whitespace-pre-wrap text-gray-600 dark:text-gray-300">{viewing.message}</p>
              </div>
              <PushNotificationDevicePreview
                title={viewing.title}
                message={viewing.message}
                imageUrl={viewing.imageUrl}
                bannerImageUrl={viewing.bannerImageUrl}
              />
            </div>
          </div>
        </div>
      ) : null}
    </PushPanelShell>
  );
};

export default PushTemplatesPanel;
